import { useAppTheme } from "@hooks/useAppTheme";
import { ImageViewerModal } from "@memorang/ui";
import { Image } from "expo-image";
import { useState } from "react";
import { Platform, Pressable, View } from "react-native";
import { Icon } from "react-native-paper";

import type { ScenarioImageSource } from "../data/scenarios";

export const ScenarioImage = ({
	image,
	maxWidth = 420,
	maxHeight = 320,
}: {
	image: ScenarioImageSource;
	maxWidth?: number;
	maxHeight?: number;
}) => {
	const theme = useAppTheme();
	const [aspectRatio, setAspectRatio] = useState<number>();
	const [viewerVisible, setViewerVisible] = useState(false);
	const label = image.title ?? "Scenario example image";

	// Bound by both width and height so a tall portrait photo (e.g. the essay,
	// which is mostly empty lined paper) doesn't dominate the page and force a
	// long scroll. The box hugs the image at its natural ratio - no letterboxing -
	// and tap-to-zoom reveals full detail.
	const ratio = aspectRatio ?? 3 / 4;
	const boxWidth = Math.min(maxWidth, maxHeight * ratio);

	return (
		<View
			style={{
				alignSelf: "center",
				width: boxWidth,
				maxWidth,
				borderWidth: 1,
				borderColor: theme.colors.outlineVariant,
				borderRadius: theme.roundness,
				overflow: "hidden",
			}}
		>
			<Pressable
				onPress={() => setViewerVisible(true)}
				accessibilityRole="imagebutton"
				accessibilityLabel={label}
				//@ts-expect-error - only cursor pointer and auto is supported in types
				style={Platform.OS === "web" ? { cursor: "zoom-in" } : undefined}
			>
				<Image
					source={{ uri: image.url }}
					contentFit="contain"
					onLoad={(event) => {
						const { width, height } = event.source ?? {};
						if (width && height) {
							setAspectRatio(width / height);
						}
					}}
					style={{ width: "100%", aspectRatio: ratio }}
					accessibilityLabel={label}
				/>
				{/* Affordance: the image is tap/click-to-enlarge (PII detail matters
				    here); surface that on every platform, not just web hover. */}
				<View
					style={{
						position: "absolute",
						right: 8,
						bottom: 8,
						flexDirection: "row",
						alignItems: "center",
						gap: 4,
						paddingHorizontal: 8,
						paddingVertical: 4,
						borderRadius: theme.roundness * 4,
						backgroundColor: "rgba(0, 0, 0, 0.55)",
					}}
					pointerEvents="none"
				>
					<Icon source="magnify-plus-outline" size={14} color="#fff" />
				</View>
			</Pressable>
			<ImageViewerModal
				visible={viewerVisible}
				images={[{ url: image.url, caption: image.title }]}
				handleClose={() => setViewerVisible(false)}
			/>
		</View>
	);
};
